import { useEffect, useState, useCallback } from "react";
import {
  RefreshCw, ArrowDownLeft, ArrowUpRight, AlertTriangle, ShieldCheck, TrendingUp,
  Wallet, Coins,
} from "lucide-react";
import { getTreasury, syncTreasury } from "@/api/entities";
import { Panel, Label, Stat, Heading, Loading, Pill, HudButton, Empty, accent } from "@/components/hud";
import ReactorGauge from "@/components/ReactorGauge";
import { useOperator } from "@/context/OperatorContext";
import { fmtMoney, fmtDateTime, timeAgo } from "@/lib/format";
import { toast } from "sonner";

export default function Treasury() {
  const { canWrite } = useOperator();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await getTreasury());
    } catch {
      toast.error("Could not load treasury");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await syncTreasury();
      toast.success(res?.stripe_configured ? "Synced with Stripe" : "Stripe not connected — nothing to sync");
      load();
    } catch {
      toast.error("Sync needs operator sign-in");
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return <Loading label="Reading treasury core…" />;
  if (!data) return null;

  const online = data.stripe_configured && data.settled_cents > 0;

  // A missing field must never render as "$0.00 set aside" — that reads as
  // "you owe nothing", which is the one wrong answer that costs real money.
  const taxReady = [data.tax_reserve_cents, data.income_tax_reserve_cents,
    data.gst_reserve_cents, data.after_tax_cents, data.tax_reserve_rate]
    .every((v) => typeof v === "number" && Number.isFinite(v));

  return (
    <div className="space-y-6 yb-rise" data-testid="treasury-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <Label>Treasury · Stripe-honest</Label>
          <h1 className="font-display text-[26px] sm:text-[34px] mt-1.5" style={{ color: "var(--yb-text)" }}>
            Treasury Core
          </h1>
        </div>
        {canWrite && (
          <HudButton variant="ghost" onClick={sync} disabled={syncing} data-testid="btn-sync-treasury">
            <RefreshCw size={14} className={syncing ? "yb-spin" : ""} /> {syncing ? "Syncing…" : "Sync Stripe"}
          </HudButton>
        )}
      </div>

      {!data.stripe_configured && (
        <Panel className="flex items-start gap-3" style={{ borderColor: "var(--yb-amber)" }}>
          <AlertTriangle size={18} style={{ color: "var(--yb-amber)" }} className="shrink-0 mt-0.5" />
          <div>
            <div className="font-head text-[14px]" style={{ color: "var(--yb-amber)" }}>Stripe not connected</div>
            <p className="text-[12px] mt-1" style={{ color: "var(--yb-text-muted)" }}>
              Every money figure below is <strong>$0.00</strong> and will stay that way until a Stripe secret key
              is configured server-side. Nothing here is simulated. Add <code className="font-mono">STRIPE_SECRET_KEY</code> to
              begin recording real settled revenue.
            </p>
          </div>
        </Panel>
      )}

      {/* reactor + settled breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel className="flex flex-col items-center justify-center" scan>
          <ReactorGauge cents={data.settled_cents} currency={data.currency} online={online} />
          <div className="font-mono text-[10px] mt-3 text-center" style={{ color: "var(--yb-text-muted)" }}>
            settled = charges − refunds · server-side · idempotent
          </div>
        </Panel>

        <div className="lg:col-span-2 grid grid-cols-2 gap-4">
          <Panel><Stat label="Settled (lifetime)" value={fmtMoney(data.settled_cents, { currency: data.currency })} sub="real, settled revenue" /></Panel>
          <Panel><Stat label="Paid · 30 days" value={fmtMoney(data.paid_30d_cents, { currency: data.currency })} accent="mint" sub="settled in window" /></Panel>
          <Panel><Stat label="Refunded (lifetime)" value={fmtMoney(data.refunded_cents, { currency: data.currency })} accent="magenta" sub="already netted out above" /></Panel>
          <Panel><Stat label="Last sync" value={timeAgo(data.last_synced)} accent="violet" sub={fmtDateTime(data.last_synced)} /></Panel>
        </div>
      </div>

      {/* tax set-aside — settled money that is owed, not earned */}
      {taxReady ? (
      <Panel style={{ borderColor: "var(--yb-amber)" }}>
        <Heading title="Tax Set-Aside" kicker="Owed at tax time · NOT yours to spend" accent="amber"
          right={<Pill tone="amber">{fmtMoney(data.tax_reserve_cents, { currency: data.currency })} reserved</Pill>} />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-3">
          <Stat label="Set aside for tax" accent="amber"
            value={fmtMoney(data.tax_reserve_cents, { currency: data.currency })}
            sub={data.gst_registered ? "GST + income tax" : "income tax"} />
          <Stat label={data.gst_registered ? "Of that, GST" : "Income tax portion"}
            value={fmtMoney(data.gst_registered ? data.gst_reserve_cents : data.income_tax_reserve_cents,
              { currency: data.currency })}
            sub={data.gst_registered ? "1/11th of GST-inclusive sales" : "GST not registered"} />
          <Stat label="Revenue after estimated tax" accent="mint"
            value={fmtMoney(data.after_tax_cents, { currency: data.currency })}
            sub="NOT a bank balance" />
        </div>
        <p className="text-[12px]" style={{ color: "var(--yb-text-muted)" }}>
          Held for <strong>{data.tax_entity}</strong> as a sole trader in Australia, estimated at{" "}
          <strong>{Math.round(data.tax_reserve_rate * 100)}%</strong>
          {data.gst_registered ? " of revenue after GST" : " of settled revenue"}. Business expenses aren't
          tracked here, so this is calculated on revenue rather than profit and deliberately reserves more
          than you are likely to owe. It is a safety margin, <strong>not tax advice</strong> — confirm the
          rate with your accountant.
        </p>
        <p className="text-[12px] mt-2" style={{ color: "var(--yb-text-muted)" }}>
          "Revenue after estimated tax" is lifetime settled revenue less this estimate. It is
          <strong> not cash you can spend</strong>: it ignores money already paid out to your bank,
          business expenses, and anything else you owe.
        </p>
      </Panel>
      ) : (
      <Panel style={{ borderColor: "var(--yb-amber)" }}>
        <Heading title="Tax Set-Aside" kicker="Estimate unavailable" accent="amber" />
        <p className="text-[12px] -mt-2" style={{ color: "var(--yb-text-muted)" }}>
          The tax set-aside could not be read from the server, so no figure is shown rather than a
          misleading zero. <strong>Do not treat settled revenue as spendable</strong> — tax is still
          owed on it. Refresh, and if this persists the treasury API needs looking at.
        </p>
      </Panel>
      )}

      {/* pipeline — explicitly separate */}
      <Panel style={{ borderColor: "var(--yb-amber)" }}>
        <Heading title="Pipeline (projected)" kicker="NOT counted as revenue" accent="amber"
          right={<Pill tone="amber">{fmtMoney(data.pipeline_cents, { currency: data.currency })} open</Pill>} />
        <p className="text-[12px] -mt-2 mb-3" style={{ color: "var(--yb-text-muted)" }}>
          <TrendingUp size={12} className="inline mr-1" style={{ color: "var(--yb-amber)" }} />
          Weighted deal value across open opportunities. This is a forecast and is never added to the
          settled total in the reactor.
        </p>
      </Panel>

      {/* verified holdings — owned balances, kept separate from Stripe revenue */}
      <Panel style={{ borderColor: "var(--yb-violet)" }}>
        <Heading title="Verified Holdings" kicker="Owned balances · NOT Stripe revenue" accent="violet"
          right={
            <Pill tone="violet">
              {(data.holdings || []).some((h) => h.aud_value_available)
                ? `${fmtMoney(data.holdings_total_cents, { currency: data.currency })} verified`
                : "$0.00"}
            </Pill>
          } />
        <p className="text-[12px] -mt-2 mb-3" style={{ color: "var(--yb-text-muted)" }}>
          <Wallet size={12} className="inline mr-1" style={{ color: "var(--yb-violet)" }} />
          Real, read-only balances the operator owns (crypto exchange / on-chain wallet / PayPal
          cash). Each value is live-fetched from a source you control — never seeded or simulated —
          and is <strong>never</strong> added to the settled reactor total above.
        </p>

        {(!data.holdings || data.holdings.length === 0) ? (
          <Empty icon={Coins} title="No holdings connected"
            hint="Add a public BTC, ETH or Solana address in Settings — or connect MetaMask/Phantom read-only — and your real balances appear here. CoinSpot and PayPal Business credentials work too. Until then this reads $0.00." />
        ) : (
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-[13px]" style={{ minWidth: 560 }}>
              <thead>
                <tr style={{ color: "var(--yb-text-muted)" }} className="font-mono text-[10px] uppercase tracking-wider text-left">
                  <th className="py-2 px-2 font-normal">Asset</th>
                  <th className="py-2 px-2 font-normal text-right">Quantity</th>
                  <th className="py-2 px-2 font-normal">Source</th>
                  <th className="py-2 px-2 font-normal">Verified</th>
                  <th className="py-2 px-2 font-normal text-right">AUD value</th>
                </tr>
              </thead>
              <tbody>
                {data.holdings.map((h, i) => (
                  <tr key={`${h.source}-${h.asset}-${i}`} style={{ borderTop: "1px solid var(--yb-hairline)" }}>
                    <td className="py-2.5 px-2 font-head" style={{ color: "var(--yb-text)" }}>{h.asset}</td>
                    <td className="py-2.5 px-2 font-mono text-[11px] text-right" style={{ color: "var(--yb-text-dim)" }}>
                      {Number(h.quantity).toLocaleString("en-AU", { maximumFractionDigits: 8 })}
                    </td>
                    <td className="py-2.5 px-2">
                      <span className="pill" style={{ color: accent("violet").c }}>
                        <Coins size={11} /> {h.source}
                      </span>
                      {h.address_short && (
                        <div className="font-mono text-[10px] mt-1" style={{ color: "var(--yb-text-muted)" }}>
                          {h.label ? `${h.label} · ` : ""}{h.address_short}
                        </div>
                      )}
                    </td>
                    <td className="py-2.5 px-2 font-mono text-[11px]" style={{ color: "var(--yb-text-muted)" }}>{timeAgo(h.verified_at)}</td>
                    <td className="py-2.5 px-2 font-mono text-right" style={{ color: h.aud_value_available ? accent("violet").c : "var(--yb-amber)" }}>
                      {h.aud_value_available
                        ? fmtMoney(h.aud_value_cents, { currency: data.currency })
                        : "price unavailable"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex items-center justify-between gap-3 flex-wrap mt-4 pt-3"
              style={{ borderTop: "1px solid var(--yb-hairline)" }}>
              <div className="text-[11px]" style={{ color: "var(--yb-text-muted)" }}>
                Net position = settled Stripe cash + verified holdings (labelled sum, real components only)
              </div>
              <div className="font-mono text-[16px]" style={{ color: "var(--yb-text)" }}>
                {fmtMoney(data.net_position_cents, { currency: data.currency })}
              </div>
            </div>
          </div>
        )}
      </Panel>

      {/* ledger */}
      <Panel>
        <Heading title="Settlement Ledger" kicker="Stripe events" />
        {(!data.ledger || data.ledger.length === 0) ? (
          <Empty icon={ArrowDownLeft} title="No settled transactions yet"
            hint="When Stripe processes a charge or refund, each event is recorded here exactly once, keyed by its Stripe object id." />
        ) : (
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-[13px]" style={{ minWidth: 520 }}>
              <thead>
                <tr style={{ color: "var(--yb-text-muted)" }} className="font-mono text-[10px] uppercase tracking-wider text-left">
                  <th className="py-2 px-2 font-normal">When</th>
                  <th className="py-2 px-2 font-normal">Type</th>
                  <th className="py-2 px-2 font-normal">Reference</th>
                  <th className="py-2 px-2 font-normal text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.ledger.map((row) => {
                  const neg = row.amount_cents < 0;
                  const tone = row.kind === "refund" ? "magenta" : row.kind === "payout" ? "violet" : "mint";
                  return (
                    <tr key={row.id} style={{ borderTop: "1px solid var(--yb-hairline)" }}>
                      <td className="py-2.5 px-2 font-mono text-[11px]" style={{ color: "var(--yb-text-muted)" }}>{fmtDateTime(row.occurred_at)}</td>
                      <td className="py-2.5 px-2">
                        <span className="pill" style={{ color: accent(tone).c }}>
                          {neg ? <ArrowUpRight size={11} /> : <ArrowDownLeft size={11} />} {row.kind}
                        </span>
                      </td>
                      <td className="py-2.5 px-2 font-mono text-[11px] truncate" style={{ color: "var(--yb-text-dim)", maxWidth: 200 }}>
                        {row.id}
                      </td>
                      <td className="py-2.5 px-2 font-mono text-right" style={{ color: accent(tone).c }}>
                        {neg ? "−" : "+"}{fmtMoney(Math.abs(row.amount_cents), { currency: data.currency })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel className="flex items-center gap-3">
        <ShieldCheck size={18} style={{ color: "var(--yb-mint)" }} />
        <p className="text-[12px]" style={{ color: "var(--yb-text-muted)" }}>
          The treasury is read-only telemetry. There are no withdrawal or transfer controls here —
          money cannot be moved from this cockpit by anyone, human or agent.
        </p>
      </Panel>
    </div>
  );
}
